import { Logger } from '@saviynt/common';
import * as Yup from 'yup';

import {
  FIELD_TYPES,
  SENSITIVE_FIELD_MASK,
  VALIDATION_TYPES,
} from '../../../../../shared/constants/global';
import { stripHtmlTags } from '../../../../../shared/utils';
import { validateStringExpression } from '../expressionHelpers';
import {
  type FieldSchema,
  findValidation,
  getFieldValidations,
  hasValidation,
  type ValidatorContext,
} from '../types';

/**
 * Parses a regex string that may be in "/pattern/flags" format or plain pattern format.
 * @param regexString - The regex string to parse (e.g., "/^https?:\\/\\/\\S*$/i" or "^https?:\\/\\/\\S*$")
 * @returns A RegExp object
 */
const parseRegexString = (regexString: string): RegExp => {
  const trimmed = regexString.trim();
  const match = trimmed.match(/^\/(.+)\/([a-z]*)$/);

  if (match) {
    // Format: "/pattern/flags"
    const [, pattern, flags] = match;

    return new RegExp(pattern, flags);
  }

  // Plain pattern without slashes
  return new RegExp(trimmed);
};

interface StringValidatorOptions {
  defaultMaxLength: number;
  useMinValidationForRequired?: boolean;
  includeMinMaxValueValidation?: boolean;
  includeExpressionValidation?: boolean;
}

/**
 * Creates a base string validator with configurable options.
 * This shared validator eliminates duplication across text, textarea, and sensitive validators.
 */
const createBaseStringValidator = (
  context: ValidatorContext,
  options: StringValidatorOptions
): FieldSchema => {
  const {
    field,
    l,
    isRequired,
    requiredMessage,
    currentValues,
    touched,
    entityData,
  } = context;

  const {
    defaultMaxLength,
    useMinValidationForRequired = false,
    includeMinMaxValueValidation = true,
    includeExpressionValidation = true,
  } = options;

  let validator = Yup.string().nullable();
  const validations = getFieldValidations(field);

  // Handle required validation
  if (isRequired && 'isReadOnly' in field && !field.isReadOnly) {
    if (useMinValidationForRequired) {
      const minValidation = findValidation(validations, 'MIN');

      if (minValidation) {
        const minValue = Number(minValidation.value);

        validator = validator.required(
          minValidation.message ??
            l('Minimum {value} characters required', { value: minValue })
        );
      } else {
        validator = validator.required(
          requiredMessage ?? l('This field is required')
        );
      }
    } else {
      validator = validator.required(
        requiredMessage ?? l('This field is required')
      );
    }
  }

  // Apply validation rules
  validations.forEach((rule) => {
    switch (rule.type) {
      case VALIDATION_TYPES.REGEX: {
        if (typeof rule.value === 'string') {
          try {
            const pattern = String(rule.value).trim();

            if (pattern.length > 0 && pattern.length <= 256) {
              const regexPattern = parseRegexString(pattern);
              const errorMsg =
                stripHtmlTags(rule.message || '') || l('Invalid format');
              const isSensitive =
                'fieldType' in field &&
                (field.fieldType === FIELD_TYPES.PASSWORD ||
                  field.fieldType === FIELD_TYPES.SENSITIVE);

              // Skip regex validation for sentinel values on password/sensitive
              // fields in update scenarios — the sentinel is not a real value
              // and will be excluded from the submission payload
              validator = validator.test(
                'regex-validation',
                errorMsg,
                (value) => {
                  if (!value) {
                    return true;
                  }

                  if (isSensitive && value === SENSITIVE_FIELD_MASK) {
                    return true;
                  }

                  return regexPattern.test(value);
                }
              );
            }
          } catch (error) {
            Logger.warn('Invalid regex pattern:', error);
          }
        }

        break;
      }

      case VALIDATION_TYPES.MIN_LENGTH: {
        const minLength = Number(rule.value);

        if (!Number.isNaN(minLength)) {
          const isSensitive =
            'fieldType' in field &&
            (field.fieldType === FIELD_TYPES.PASSWORD ||
              field.fieldType === FIELD_TYPES.SENSITIVE);

          validator = validator.test(
            'min-length',
            stripHtmlTags(rule.message ?? '') ||
              l('Minimum {value} characters required', { value: minLength }),
            (value) => {
              if (!value) {
                return true;
              }

              // Skip min-length for sentinel values on password/sensitive fields
              if (isSensitive && value === SENSITIVE_FIELD_MASK) {
                return true;
              }

              return value.length >= minLength;
            }
          );
        }

        break;
      }

      case VALIDATION_TYPES.MAX_LENGTH: {
        const maxLength = Number(rule.value);

        if (!Number.isNaN(maxLength)) {
          const isSensitive =
            'fieldType' in field &&
            (field.fieldType === FIELD_TYPES.PASSWORD ||
              field.fieldType === FIELD_TYPES.SENSITIVE);

          validator = validator.test(
            'max-length',
            stripHtmlTags(rule.message ?? '') ||
              l('Maximum length is {value} characters', { value: maxLength }),
            (value) => {
              if (!value) {
                return true;
              }

              // Skip max-length for sentinel values on password/sensitive fields
              if (isSensitive && value === SENSITIVE_FIELD_MASK) {
                return true;
              }

              return value.length <= maxLength;
            }
          );
        }

        break;
      }

      case VALIDATION_TYPES.MIN_VALUE: {
        if (includeMinMaxValueValidation) {
          const minVal = Number(rule.value);

          if (!Number.isNaN(minVal) && minVal > 0) {
            const isSensitive =
              'fieldType' in field &&
              (field.fieldType === FIELD_TYPES.PASSWORD ||
                field.fieldType === FIELD_TYPES.SENSITIVE);

            validator = validator.test(
              'min-validation',
              stripHtmlTags(rule.message || '') ||
                l('Minimum {value} characters required', { value: minVal }),
              (value) => {
                if (!value) {
                  return true;
                }

                if (isSensitive && value === SENSITIVE_FIELD_MASK) {
                  return true;
                }

                return value.length >= minVal;
              }
            );
          }
        }

        break;
      }

      case VALIDATION_TYPES.MAX_VALUE: {
        if (includeMinMaxValueValidation) {
          const maxVal = Number(rule.value);

          if (!Number.isNaN(maxVal) && maxVal > 0) {
            const isSensitive =
              'fieldType' in field &&
              (field.fieldType === FIELD_TYPES.PASSWORD ||
                field.fieldType === FIELD_TYPES.SENSITIVE);

            validator = validator.test(
              'max-validation',
              stripHtmlTags(rule.message || '') ||
                l('Maximum {value} characters allowed', { value: maxVal }),
              (value) => {
                if (!value) {
                  return true;
                }

                if (isSensitive && value === SENSITIVE_FIELD_MASK) {
                  return true;
                }

                return value.length <= maxVal;
              }
            );
          }
        }

        break;
      }

      case VALIDATION_TYPES.EXPRESSION: {
        if (
          includeExpressionValidation &&
          rule.value &&
          typeof rule.value === 'string'
        ) {
          const ruleValue = rule.value;

          validator = validator.test(
            'expression-validation',
            stripHtmlTags(rule.message || '') || l('Validation failed'),
            (value) =>
              validateStringExpression(
                value ?? undefined,
                ruleValue,
                field.fieldName,
                {
                  values: currentValues ?? {},
                  touched,
                  entityData: entityData ?? null,
                }
              )
          );
        }

        break;
      }

      default:
        break;
    }
  });

  // Apply default max limit if no MAX_LENGTH validation
  if (!hasValidation(validations, VALIDATION_TYPES.MAX_LENGTH)) {
    validator = validator.max(
      defaultMaxLength,
      l('Maximum length is {value} characters', {
        value: defaultMaxLength,
      })
    );
  }

  return validator;
};

export default createBaseStringValidator;
